import React, { useState, useEffect, useRef, useCallback } from "react";

interface TooltipProps {
  children: React.ReactNode;
  text: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  alwaysVisible?: boolean;
  shortcut?: string;
  className?: string;
  style?: React.CSSProperties;
  align?: "start" | "center" | "end"; // horizontal align for top/bottom
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  text,
  position = "top",
  delay = 500,
  alwaysVisible = false,
  shortcut,
  className,
  style,
  align = "center",
}) => {
  const [showTooltip, setShowTooltip] = useState(alwaysVisible);
  const [enterTimeoutId, setEnterTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );
  const [leaveTimeoutId, setLeaveTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [adjustedAlign, setAdjustedAlign] = useState(align);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [longPressTimeoutId, setLongPressTimeoutId] =
    useState<NodeJS.Timeout | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateOptimalPosition = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8; // Safe margin from viewport edge

    // Calculate tooltip dimensions more accurately
    const textLength = typeof text === "string" ? text.length : 20; // fallback for React nodes
    const tooltipWidth = Math.min(textLength * 8 + 24, 200); // More accurate width calculation
    const tooltipHeight = 40; // approximate height

    // Start with the preferred position and alignment
    let newPosition = position;
    let newAlign = align;

    // For buttons in corners, prioritize horizontal positioning over vertical
    const isNearRightEdge = containerRect.right > viewportWidth - 100;
    // const isNearLeftEdge = containerRect.left < 100;
    // const isNearTopEdge = containerRect.top < 100;
    // const isNearBottomEdge = containerRect.bottom > viewportHeight - 100;

    // Simplified and deterministic positioning logic
    // Always try to keep the original position first, only adjust alignment
    if (position === "bottom" || position === "top") {
      // For vertical tooltips, calculate optimal alignment
      const centerX = containerRect.left + containerRect.width / 2;
      const tooltipLeft = centerX - tooltipWidth / 2;
      const tooltipRight = centerX + tooltipWidth / 2;

      if (tooltipLeft < margin) {
        // Would overflow left, use start alignment
        newAlign = "start";
      } else if (tooltipRight > viewportWidth - margin) {
        // Would overflow right, use end alignment
        newAlign = "end";
      } else {
        // Fits with center alignment
        newAlign = "center";
      }

      // Only switch to horizontal if vertical really doesn't work
      const tooltipTop = containerRect.bottom + tooltipHeight;
      const tooltipBottom = containerRect.top - tooltipHeight;

      if (position === "bottom" && tooltipTop > viewportHeight - margin) {
        // Bottom overflows, try top
        if (tooltipBottom < margin) {
          // Top also overflows, switch to horizontal
          newPosition = isNearRightEdge ? "left" : "right";
          newAlign = "center";
        } else {
          newPosition = "top";
        }
      } else if (position === "top" && tooltipBottom < margin) {
        // Top overflows, try bottom
        if (tooltipTop > viewportHeight - margin) {
          // Bottom also overflows, switch to horizontal
          newPosition = isNearRightEdge ? "left" : "right";
          newAlign = "center";
        } else {
          newPosition = "bottom";
        }
      }
    } else if (position === "left" || position === "right") {
      // For horizontal tooltips, check if they fit
      if (position === "left") {
        const tooltipRight = containerRect.left - tooltipWidth;
        if (tooltipRight < margin) {
          // Left overflows, switch to right
          newPosition = "right";
        }
      } else if (position === "right") {
        const tooltipLeft = containerRect.right + tooltipWidth;
        if (tooltipLeft > viewportWidth - margin) {
          // Right overflows, switch to left
          newPosition = "left";
        }
      }
    }

    setAdjustedPosition(newPosition);
    setAdjustedAlign(newAlign);
  }, [position, align, text]);

  const handleMouseEnter = () => {
    if (alwaysVisible || isTouchDevice) return; // Don't change visibility if always visible or on touch devices

    // Clear any pending leave timeout
    if (leaveTimeoutId) {
      clearTimeout(leaveTimeoutId);
      setLeaveTimeoutId(null);
    }

    // Calculate optimal position BEFORE showing tooltip
    calculateOptimalPosition();

    // Set enter timeout if delay is specified
    if (delay > 0) {
      const id = setTimeout(() => {
        setShowTooltip(true);
      }, delay);
      setEnterTimeoutId(id);
    } else {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (alwaysVisible || isTouchDevice) return; // Don't change visibility if always visible or on touch devices

    // Clear any pending enter timeout
    if (enterTimeoutId) {
      clearTimeout(enterTimeoutId);
      setEnterTimeoutId(null);
    }

    // Set leave timeout for smooth transition
    const id = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
    setLeaveTimeoutId(id);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (enterTimeoutId) clearTimeout(enterTimeoutId);
      if (leaveTimeoutId) clearTimeout(leaveTimeoutId);
      if (longPressTimeoutId) clearTimeout(longPressTimeoutId);
    };
  }, [enterTimeoutId, leaveTimeoutId, longPressTimeoutId]);

  // Handle window resize to recalculate position
  useEffect(() => {
    const handleResize = () => {
      if (showTooltip) {
        calculateOptimalPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showTooltip, position, align, calculateOptimalPosition]);

  // Reset adjusted position when props change
  useEffect(() => {
    setAdjustedPosition(position);
    setAdjustedAlign(align);
  }, [position, align]);

  // Detect touch device
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        "ontouchstart" in window || navigator.maxTouchPoints > 0
      );
    };

    checkTouchDevice();
    window.addEventListener("resize", checkTouchDevice);
    return () => window.removeEventListener("resize", checkTouchDevice);
  }, []);

  // Touch event handlers for long press
  const handleTouchStart = () => {
    if (alwaysVisible || !isTouchDevice) return;

    // Clear any existing long press timeout
    if (longPressTimeoutId) {
      clearTimeout(longPressTimeoutId);
    }

    // Set long press timeout (800ms)
    const id = setTimeout(() => {
      calculateOptimalPosition();
      setShowTooltip(true);
    }, 800);
    setLongPressTimeoutId(id);
  };

  const handleTouchEnd = () => {
    if (alwaysVisible || !isTouchDevice) return;

    // Clear long press timeout if touch ends before timeout
    if (longPressTimeoutId) {
      clearTimeout(longPressTimeoutId);
      setLongPressTimeoutId(null);
    }

    // Hide tooltip after a short delay when touch ends
    if (showTooltip) {
      setTimeout(() => {
        setShowTooltip(false);
      }, 2000); // Show tooltip for 2 seconds on mobile
    }
  };

  const handleTouchMove = () => {
    if (alwaysVisible || !isTouchDevice) return;

    // Cancel long press if user moves finger
    if (longPressTimeoutId) {
      clearTimeout(longPressTimeoutId);
      setLongPressTimeoutId(null);
    }
  };

  const getTooltipStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      position: "absolute",
      padding: "8px 12px",
      background: "var(--toggle-bg)",
      color: "var(--text-secondary)",
      borderRadius: "12px",
      fontSize: "clamp(11px, 3vw, 14px)",
      fontWeight: "normal",
      textAlign: "center",
      whiteSpace: "normal",
      maxWidth: "200px",
      width: "max-content",
      zIndex: 1000,
      border: "none",
      backdropFilter: "blur(7px)",
      WebkitBackdropFilter: "blur(7px)",
      lineHeight: "1.4",
      wordWrap: "break-word",
      overflowWrap: "break-word",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      pointerEvents: "none",
      userSelect: "none",
    };

    // Consistent slide-up animation regardless of position
    const slideInTransform = showTooltip
      ? "translateY(0) scale(1)"
      : "translateY(10px) scale(0.95)";
    const opacity = showTooltip ? 1 : 0;

    switch (adjustedPosition) {
      case "top":
        return adjustedAlign === "center"
          ? {
              ...baseStyles,
              bottom: "calc(100% + 8px)",
              left: "50%",
              transform: `translateX(-50%) ${slideInTransform}`,
              opacity,
            }
          : adjustedAlign === "start"
          ? {
              ...baseStyles,
              bottom: "calc(100% + 8px)",
              left: 0,
              transform: slideInTransform,
              opacity,
            }
          : {
              ...baseStyles,
              bottom: "calc(100% + 8px)",
              right: 0,
              transform: slideInTransform,
              opacity,
            };
      case "bottom":
        return adjustedAlign === "center"
          ? {
              ...baseStyles,
              top: "calc(100% + 8px)",
              left: "50%",
              transform: `translateX(-50%) ${slideInTransform}`,
              opacity,
            }
          : adjustedAlign === "start"
          ? {
              ...baseStyles,
              top: "calc(100% + 8px)",
              left: 0,
              transform: slideInTransform,
              opacity,
            }
          : {
              ...baseStyles,
              top: "calc(100% + 8px)",
              right: 0,
              transform: slideInTransform,
              opacity,
            };
      case "left":
        return {
          ...baseStyles,
          right: "calc(100% + 8px)",
          top: "50%",
          transform: `translateY(-50%) ${slideInTransform}`,
          opacity,
        };
      case "right":
        return {
          ...baseStyles,
          left: "calc(100% + 8px)",
          top: "50%",
          transform: `translateY(-50%) ${slideInTransform}`,
          opacity,
        };
      default:
        return {
          ...baseStyles,
          opacity,
          transform: slideInTransform,
        };
    }
  };

  const tooltipContent = shortcut ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        flexWrap: "wrap",
      }}
    >
      <kbd
        style={{
          background: "var(--kbd-bg)",
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "11px",
          fontFamily: "monospace",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {shortcut}
      </kbd>
      <span style={{ flex: 1 }}>{text}</span>
    </div>
  ) : (
    text
  );

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "relative",
        display: "inline-block",
        ...(style || {}),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      {children}
      <div ref={tooltipRef} style={getTooltipStyles()}>
        {tooltipContent}
      </div>
    </div>
  );
};
